import { React, useRef, useState, useLayoutEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

import * as THREE from "three";

/*
VirtualReality > Curve_left > Curve_right > (Lines)
*/

function CurveLeft({
  rayonCurve,
  vehicule_width,
  angle_traj,
  isClockwise,
  color,
  position,
}) {
  const clockwise = isClockwise; // Défini sens de rotation de ElipseCurve
  const geometry = useRef();

  const [curve_left_100_position, setCurve_left_100_position] = useState(0);
  const [curve_left_75_position, setCurve_left_75_position] = useState(0);
  const [curve_left_50_position, setCurve_left_50_position] = useState(0);
  const [curve_left_25_position, setCurve_left_25_position] = useState(0);
  const [curve_left_0_position, setCurve_left_0_position] = useState(0);

  const curve_position = {
    curve_left_100_position: curve_left_100_position,
    curve_left_75_position: curve_left_75_position,
    curve_left_50_position: curve_left_50_position,
    curve_left_25_position: curve_left_25_position,
    curve_left_0_position: curve_left_0_position,
  };

  useLayoutEffect(() => {
    const curve = new THREE.EllipseCurve(
      -rayonCurve,
      0, // ax, aY
      rayonCurve - vehicule_width,
      rayonCurve - vehicule_width, // xRadius, yRadius
      0,
      angle_traj, // aStartAngle, aEndAngle
      clockwise, // aClockwise
      0 // aRotation
    );
    const points = curve.getPoints(50);
    geometry.current.setFromPoints(points);

    setCurve_left_100_position(curve.getPointAt(1));
    setCurve_left_75_position(curve.getPointAt(0.75));
    setCurve_left_50_position(curve.getPointAt(0.5));
    setCurve_left_25_position(curve.getPointAt(0.25));
    setCurve_left_0_position(curve.getPointAt(0));
  }, [angle_traj, clockwise, rayonCurve, vehicule_width]);

  return (
    <line position={[position[0], position[1], position[2]]}>
      <bufferGeometry ref={geometry} />
      <lineBasicMaterial color={color} />
      <CurveRight
        color={color}
        rayonCurve={rayonCurve}
        vehicule_width={vehicule_width}
        angle_traj={angle_traj}
        curve_left_position={curve_position}
        isClockwise={isClockwise}
      />
    </line>
  );
}

function CurveRight({
  rayonCurve,
  vehicule_width,
  angle_traj,
  curve_left_position,
  isClockwise,
  color,
}) {
  const clockwise = isClockwise;
  const geometry = useRef();
  const [curve_right_100_position, setCurve_right_100_position] = useState(0);
  const [curve_right_75_position, setCurve_right_75_position] = useState(0);
  const [curve_right_50_position, setCurve_right_50_position] = useState(0);
  const [curve_right_25_position, setCurve_right_25_position] = useState(0);
  const [curve_right_0_position, setCurve_right_0_position] = useState(0);

  useLayoutEffect(() => {
    const curve = new THREE.EllipseCurve(
      -rayonCurve,
      0, // ax, aY
      rayonCurve + vehicule_width,
      rayonCurve + vehicule_width, // xRadius, yRadius
      0,
      angle_traj, // aStartAngle, aEndAngle
      clockwise, // aClockwise
      0 // aRotation
    );
    const points = curve.getPoints(50);
    geometry.current.setFromPoints(points);
    setCurve_right_100_position(curve.getPointAt(1));
    setCurve_right_75_position(curve.getPointAt(0.75));
    setCurve_right_50_position(curve.getPointAt(0.5));
    setCurve_right_25_position(curve.getPointAt(0.25));
    setCurve_right_0_position(curve.getPointAt(0));
  }, [angle_traj, clockwise, rayonCurve, vehicule_width]);

  return (
    <line position={[0, 0, 0]}>
      <bufferGeometry ref={geometry} />
      <lineBasicMaterial color={color} />

      <Line
        curve_left_position={curve_left_position.curve_left_100_position}
        curve_right_position={curve_right_100_position}
        color="crimson"
      />

      <Line
        curve_left_position={curve_left_position.curve_left_75_position}
        curve_right_position={curve_right_75_position}
        color="crimson"
      />
      <Line
        curve_left_position={curve_left_position.curve_left_50_position}
        curve_right_position={curve_right_50_position}
        color="crimson"
      />
      <Line
        curve_left_position={curve_left_position.curve_left_25_position}
        curve_right_position={curve_right_25_position}
        color="crimson"
      />
      <Line
        curve_left_position={curve_left_position.curve_left_0_position}
        curve_right_position={curve_right_0_position}
        color="crimson"
      />
    </line>
  );
}

function Line({ curve_left_position, curve_right_position, color }) {
  const geometry = useRef();

  useLayoutEffect(() => {
    if (curve_left_position !== 0 && curve_right_position !== 0) {
      // Dès fois le callback du useEffect s'effectue avant d'avoir reçu la valeur. Le callback est rappelé avec les dependencies.
      const curve = new THREE.LineCurve(
        curve_left_position,
        curve_right_position
      );
      const points = curve.getPoints(1);
      geometry.current.setFromPoints(points);
    }
  }, [curve_left_position, curve_right_position]);

  return (
    <line position={[0, 0, 0]}>
      <bufferGeometry ref={geometry} />
      <lineBasicMaterial color={color} />
    </line>
  );
}

export default function VirtualReality({
  wheel_angle,
  polar,
  driveMode,
  zoom,
  position,
}) {
  //polar définit l'angle polaire de la perspective
  const vehicule_parameters = {
    //Paramètres lié au véhicule
    vehicule_length: 2,
    length_curve: 20,
    vehicule_width: 5.5,
  };

  if (wheel_angle === 0) {
    // Avoid division by zero
    wheel_angle = 0.01;
  }

  let rayonCurve =
    vehicule_parameters.vehicule_length /
    Math.tan(wheel_angle);

  if (driveMode === 1) {
    //Opposite
    rayonCurve = rayonCurve * 2.8; //Gain lié à l'effet de perspective.
  }

  if (driveMode === 2) {
    //Regular
    rayonCurve = rayonCurve * 3.9; //Gain lié à l'effet de perspective.
  }



  let angle_traj = vehicule_parameters.length_curve / rayonCurve;

  //console.log((wheel_angle*180/3.14), driveMode)

  if (driveMode !== 3) {
    return (
      <Canvas className="augmentedReality" style={{position: "absolute", width: "100%", height: "100%", top: "30%"}}>
        <OrbitControls
          maxDistance={zoom}
          minDistance={zoom}
          maxAzimuthAngle={0}
          minAzimuthAngle={0}
          maxPolarAngle={polar}
          minPolarAngle={polar}
        />
        <CurveLeft
          color="crimson"
          rayonCurve={rayonCurve}
          vehicule_width={vehicule_parameters.vehicule_width}
          angle_traj={angle_traj}
          isClockwise={wheel_angle > 0 ? false : true}
          position={position}
        />
      </Canvas>
    );
  } else {
    wheel_angle = wheel_angle * -1;
    let width = vehicule_parameters.vehicule_width * 1.2; // Facteur de correction
    return (
      <Canvas className="augmentedReality" style={{position: "absolute", width: "100%", height: "100%", top: "30%"}}>
        <OrbitControls
          maxDistance={zoom}
          minDistance={zoom}
          maxAzimuthAngle={0}
          minAzimuthAngle={0}
          maxPolarAngle={polar}
          minPolarAngle={polar}
        />
        <Line
          curve_left_position={
            new THREE.Vector2(-width, 0)
          }
          curve_right_position={
            new THREE.Vector2(width, 0)
          }
          color="crimson"
        />
        <Line
          curve_right_position={
            new THREE.Vector2(-width, 0)
          }
          curve_left_position={
            new THREE.Vector2(
              -width +
                vehicule_parameters.length_curve *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />
        <Line
          curve_right_position={
            new THREE.Vector2(width, 0)
          }
          curve_left_position={
            new THREE.Vector2(
              width +
                vehicule_parameters.length_curve *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />
        <Line
          curve_right_position={
            new THREE.Vector2(
              -width +
                vehicule_parameters.length_curve *
                  0.25 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.25 *
                Math.cos(wheel_angle)
            )
          }
          curve_left_position={
            new THREE.Vector2(
              width +
                vehicule_parameters.length_curve *
                  0.25 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.25 *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />

        <Line
          curve_right_position={
            new THREE.Vector2(
              -width +
                vehicule_parameters.length_curve *
                  0.5 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.5 *
                Math.cos(wheel_angle)
            )
          }
          curve_left_position={
            new THREE.Vector2(
              width +
                vehicule_parameters.length_curve *
                  0.5 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.5 *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />

        <Line
          curve_right_position={
            new THREE.Vector2(
              -width +
                vehicule_parameters.length_curve *
                  0.75 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.75 *
                Math.cos(wheel_angle)
            )
          }
          curve_left_position={
            new THREE.Vector2(
              width +
                vehicule_parameters.length_curve *
                  0.75 *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                0.75 *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />
        <Line
          curve_right_position={
            new THREE.Vector2(
              -width +
                vehicule_parameters.length_curve *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                Math.cos(wheel_angle)
            )
          }
          curve_left_position={
            new THREE.Vector2(
              width +
                vehicule_parameters.length_curve *
                  Math.sin(wheel_angle),
              vehicule_parameters.length_curve *
                Math.cos(wheel_angle)
            )
          }
          color="crimson"
        />
      </Canvas>
    );
  }
}

//
